import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// Data fetching/state management
import { useLayoutPreferences } from "@/stores/layoutPreferences";
import { useEntityData } from "@/hooks/useEntityData";

// UI Components
import EntityGrid from "@/components/entity/EntityGrid";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import MapView from "@/components/map-view/MapView";
import EntityCalendarView from "@/components/calendar/EntityCalendarView";
import SimpleKanbanBoard from "@/components/kanban/SimpleKanbanBoard";
import ImageGalleryOverlay from "@/components/ui/ImageGalleryOverlay";
import { File } from "@/types/breeze/File";
import EntityChartsView from "@/components/entity/EntityChartsView";
import ImportDialog from "@/components/entity/ImportDialog";

// Icons
import {
  Plus,
  LayoutGrid,
  Kanban,
  Map,
  Calendar,
  BarChart3,
  Trash,
  Upload,
} from "lucide-react";

// Utils
import { formatAnyName, singularize } from "@/utils/text";
import { anyService } from "@/services/breeze/AnyService";
import { CreateUpdateAny } from "@/types/breeze/Any";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import PageTemplate from "@/components/page-template/PageTemplate";
import { Box, Button, Flex, Switch, Text } from "@radix-ui/themes";
import * as Toolbar from "@radix-ui/react-toolbar";
import { useAuth } from "@/providers/AuthProvider";
import ErrorCard from "@/components/ui/ErrorCard";

export default function EntityOverviewPage() {
  const params = useParams();
  const entityName = params.entityName as string;
  const singularEntityName = singularize(formatAnyName(entityName));
  const { currentTenant, safeNavigate } = useAuth();
  const confirm = useConfirm();

  // Use layout preferences store
  const { getLayout, setLayout } = useLayoutPreferences();
  const viewType = getLayout(entityName);

  const {
    entityDefinition,
    entityTree,
    entityData,
    isLoading,
    error,
    selectedFields,
    selectedItems,
    page,
    pageSize,
    setSelectedFields,
    setSelectedItems,
    setPage,
    setPageSize,
    setFilterQuery,
    setSort,
    setViewType,
    deleteItems,
  } = useEntityData(entityName);

  const { data: entityPermissions, isLoading: isEntityPermissionsLoading } =
    useQuery({
      queryKey: ["entity-permissions", entityName],
      queryFn: () => anyService.getEntityLevelPermissions(entityName),
    });

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [instantEditEnabled, setInstantEditEnabled] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Gallery state for universal use (moved from UniversalDrawer to prevent event conflicts)
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex] = useState(0);
  const [currentViewFiles] = useState<File[]>([]);

  /*
  const _openGallery = (index: number, files: File[]) => {
    // Filter to only images and find the correct index
    const imageFiles = files.filter((file) =>
      file.file_type.startsWith("image/"),
    );
    const selectedFile = files[index];
    const imageIndex = imageFiles.findIndex(
      (img) => img.id === selectedFile.id,
    );
    setSelectedImageIndex(imageIndex >= 0 ? imageIndex : 0);
    setCurrentViewFiles(files); // Store current view files for gallery
    setGalleryOpen(true);
  };
  */

  const queryClient = useQueryClient();

  // Mutation for inline cell updates (separate from drawer updates)
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      field,
      value,
    }: {
      id: number;
      field: string;
      value: unknown;
    }) => {
      return anyService.updateAny(
        entityName,
        id,
        { [field]: value } as CreateUpdateAny,
        true,
      );
    },
    onSuccess: (_, variables) => {
      toast.success(`Updated ${variables.field} successfully`);

      // Invalidate relevant queries
      queryClient.removeQueries({
        queryKey: ["entity-data", entityName, variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["entities-data", entityName],
      });
    },
    onError: (error: unknown) => {
      console.error("Failed to update entity:", error);
      toast.error("Failed to save changes");
    },
  });

  // Reset initial load complete when entity changes
  useEffect(() => {
    setInitialLoadComplete(false);
  }, [entityName]);

  useEffect(() => {
    // Set initial load complete when loading is done, regardless of success or error
    if (!isLoading && !isEntityPermissionsLoading) {
      setInitialLoadComplete(true);
    }
  }, [isLoading, isEntityPermissionsLoading]);

  // Update view type in useEntityData when layout changes
  useEffect(() => {
    setViewType(viewType);
  }, [viewType, setViewType]);

  const handleCellValueChanged = (
    field: string,
    newValue: unknown,
    data: Record<string, unknown>,
  ) => {
    // Only proceed if we have an ID
    if (!data.id) {
      return;
    }

    // Always save the change - AG Grid already determined it's different

    updateMutation.mutate({
      id: data.id as number,
      field,
      value: newValue,
    });
  };

  const handleDeleteItems = async () => {
    const confirmed = await confirm({
      title: "Confirm Delete",
      message: `Are you sure you want to delete ${selectedItems.length} items? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteItems(selectedItems);
    } catch {
      await confirm({
        title: "Error",
        message: `Failed to delete some items. Please try again.`,
        confirmText: "OK",
        variant: "danger",
      });
    }
  };

  const renderView = () => {
    // At this point, we know we have valid data since error handling is done at component level
    if (!entityDefinition || !entityTree) {
      return null; // This should never happen now, but just in case
    }

    switch (viewType) {
      case "grid":
        return (
          <EntityGrid
            entityDefinition={entityDefinition}
            entityData={entityData}
            currentPage={page}
            currentPageSize={pageSize}
            isLoading={isLoading}
            selectedFields={selectedFields}
            entityTree={entityTree}
            onCellClicked={(data) => {
              safeNavigate(
                `/org/${currentTenant?.id}/entities/${entityName}/${data.id}`,
              );
            }}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onFilterChange={setFilterQuery}
            onSortChange={setSort}
            onSelectionChanged={(rows) =>
              setSelectedItems(rows.map((row) => row.id))
            }
            onFieldsChanged={setSelectedFields}
            enableInstantEdit={instantEditEnabled}
            onCellValueChanged={handleCellValueChanged}
            onRelationshipClicked={(
              _fieldName,
              _currentValue,
              _targetEntity,
              _relationshipId,
              _rowData,
            ) => {
              // TODO: implement
            }}
            onFileClicked={async (_fieldName, _currentValue, _rowData) => {
              // TODO: implement
            }}
            onMultiSelectClicked={(
              _fieldName,
              _currentValue,
              _selectOptions,
              _rowData,
            ) => {
              // TODO: implement
            }}
          />
        );
      case "kanban":
        return (
          <SimpleKanbanBoard
            entityDefinition={entityDefinition}
            entityData={entityData}
            isLoading={isLoading}
          />
        );
      case "map":
        return (
          <MapView
            entityName={entityName}
            data={entityData?.items ?? []}
            isLoading={isLoading}
            availableFields={entityDefinition.fields.map((field) => ({
              key: field.name,
              title: field.name,
            }))}
            onItemClick={(id: number) => {
              safeNavigate(
                `/org/${currentTenant?.id}/entities/${entityName}/${id}`,
              );
            }}
          />
        );
      case "calendar":
        return <EntityCalendarView entityName={entityName} />;
      case "charts":
        return (
          <EntityChartsView
            entityDefinition={entityDefinition}
            entityData={entityData}
            isLoading={isLoading}
          />
        );
      default:
        return <Box>Unknown view type: {viewType}</Box>;
    }
  };

  // Show loading screen during initial load
  if (!initialLoadComplete && (isLoading || isEntityPermissionsLoading)) {
    return <LoadingScreen fullPage={false} />;
  }

  // Show error state if there's an error after initial load complete
  if (initialLoadComplete && error) {
    return <ErrorCard error={error.message || error.toString()} />;
  }

  // Show error if we've completed initial load but don't have required data
  if (initialLoadComplete && (!entityDefinition || !entityTree)) {
    return <ErrorCard error="Missing entity definition or tree data" />;
  }

  return (
    <PageTemplate title={formatAnyName(entityName)}>
      <Flex justify="between" align="center" mb="4">
        <Flex align="center" gap="4">
          <Box
            style={{
              border: "1px solid var(--gray-5)",
              borderRadius: "var(--radius-2)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <button
              onClick={() => setLayout(entityName, "grid")}
              style={{
                backgroundColor:
                  viewType === "grid" ? "var(--gray-3)" : "transparent",
                cursor: "pointer",
                padding: "0.5rem",
              }}
              title="Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLayout(entityName, "kanban")}
              style={{
                backgroundColor:
                  viewType === "kanban" ? "var(--gray-3)" : "transparent",
                cursor: "pointer",
                padding: "0.5rem",
              }}
              title="Kanban View"
            >
              <Kanban className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLayout(entityName, "map")}
              style={{
                backgroundColor:
                  viewType === "map" ? "var(--gray-3)" : "transparent",
                cursor: "pointer",
                padding: "0.5rem",
              }}
              title="Map View"
            >
              <Map className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLayout(entityName, "calendar")}
              style={{
                backgroundColor:
                  viewType === "calendar" ? "var(--gray-3)" : "transparent",
                cursor: "pointer",
                padding: "0.5rem",
              }}
              title="Calendar View"
            >
              <Calendar className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLayout(entityName, "charts")}
              style={{
                backgroundColor:
                  viewType === "charts" ? "var(--gray-3)" : "transparent",
                cursor: "pointer",
                padding: "0.5rem",
              }}
              title="Charts View"
            >
              <BarChart3 className="w-4 h-4" />
            </button>
          </Box>
        </Flex>

        <Toolbar.Root asChild>
          <Flex align="center" justify="end" gap="2">
            {viewType === "grid" && (
              <Flex align="center" gap="2" mr="2">
                <Text size="2" color="gray">
                  Instant Edit
                </Text>
                <Switch
                  checked={instantEditEnabled}
                  onCheckedChange={setInstantEditEnabled}
                  size="1"
                />
              </Flex>
            )}

            {viewType !== "charts" && entityPermissions?.delete && (
              <Toolbar.Button asChild>
                <Button
                  color="red"
                  variant="solid"
                  size="1"
                  onClick={handleDeleteItems}
                  disabled={selectedItems.length === 0}
                >
                  <Trash className="w-4 h-4" />
                </Button>
              </Toolbar.Button>
            )}

            {viewType !== "charts" && entityPermissions?.create && (
              <>
                <Toolbar.Button asChild>
                  <Button
                    variant="outline"
                    size="1"
                    onClick={() => setIsImportOpen(true)}
                  >
                    <Upload className="w-4 h-4" />
                    Import
                  </Button>
                </Toolbar.Button>

                <Toolbar.Button asChild>
                  <Button
                    variant="solid"
                    size="1"
                    onClick={() =>
                      safeNavigate(
                        `/org/${currentTenant?.id}/entities/${entityName}/create`,
                      )
                    }
                  >
                    <Plus className="w-4 h-4" />
                    Add {singularEntityName}
                  </Button>
                </Toolbar.Button>
              </>
            )}
          </Flex>
        </Toolbar.Root>
      </Flex>

      {renderView()}

      {/* Gallery overlay outside drawer to prevent event conflicts */}
      {galleryOpen && (
        <ImageGalleryOverlay
          files={currentViewFiles.filter((file) =>
            file.file_type.startsWith("image/"),
          )}
          initialIndex={selectedImageIndex}
          onClose={() => setGalleryOpen(false)}
        />
      )}

      {isImportOpen && entityDefinition && (
        <ImportDialog
          entityName={entityName}
          entityDefinition={entityDefinition}
          open={isImportOpen}
          onOpenChange={(open) => {
            setIsImportOpen(open);
            if (!open) {
              queryClient.invalidateQueries({
                queryKey: ["entities-data", entityName],
              });
            }
          }}
          onImportCompleted={() => {
            queryClient.invalidateQueries({
              queryKey: ["entities-data", entityName],
            });
          }}
        />
      )}
    </PageTemplate>
  );
}
